import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import { getLoginUserData } from "../../../services/account.service";
import { ROLES } from "../../../constants/general.constant";
import * as Yup from "yup";
import {
  Box,
  Button,
  Fade,
  Grow,
  Tooltip,
  CircularProgress,
  Snackbar
} from "@mui/material";
import AddBusinessDialog from "./addBusiness";
import BusinessTable from "./BusinessTable";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import {
  getCustomerBusinesses,
  updateCustomerBusiness,
  createCustomerBusiness,
} from "../../../services/customer.service";
import Loader from "../../../common/loader";
import { Business } from "../../../constants/business.constant";
import {commonMessage} from "../../../constants/message.constant";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import GuidedTutorialOverlay, {
  GuidedTutorialStep,
} from "../../common/GuidedTutorialOverlay";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  },
);

const MyBusinesses: React.FC = () => {
  const addBusinessButtonRef = useRef<HTMLButtonElement | null>(null);
  const businessCardRef = useRef<HTMLDivElement | null>(null);
  const newLoanButtonRef = useRef<HTMLButtonElement | null>(null);
  const uploadSectionRef = useRef<HTMLDivElement | null>(null);
  const loanDetailsSectionRef = useRef<HTMLDivElement | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [paginationData, setPaginationData] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [newBusinessId, setNewBusinessId] = useState<string>('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialFetch = useRef(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });

  const demoBusiness: Business = {
    id: "tutorial-business",
    companyName: "Sample Business LLC",
    street: "100 Main Street",
    city: "Austin",
    state: "TX",
    zipCode: "78701",
    ownershipDetails: "100% owned by applicant",
    taxIdEin: "12-3456789",
    registrationType: "Registered",
    businessPurpose: "Purchase",
  };

  const tutorialBusinesses = businesses.length ? businesses : [demoBusiness];
  const isTutorialDemo = tutorialOpen && businesses.length === 0;
  const displayedBusinesses = isTutorialDemo ? tutorialBusinesses : businesses;
  const tutorialSteps: GuidedTutorialStep[] = [
    {
      title: "Create a Business",
      description:
        "Start by adding a business profile. Enter the company details, address, ownership, registration status, and loan purpose.",
      targetRef: addBusinessButtonRef,
    },
    {
      title: "Review Business Details",
      description:
        "Each business appears as a card with its address, registration status, ownership, and selected loan purpose.",
      targetRef: businessCardRef,
    },
    {
      title: "Create a Loan",
      description:
        "Use New Loan to create a loan request for this business. The request collects loan type, loan size, proceeds, and related deal details.",
      targetRef: newLoanButtonRef,
    },
    {
      title: "Upload Documents",
      description:
        "Upload the required business documents here. Document requirements change based on the business purpose such as Purchase, Refinance, or Startup.",
      targetRef: uploadSectionRef,
    },
    {
      title: "Loan Details",
      description:
        "Use Loan Details to review created loan requests, status, DSC information, assigned bankers, and lender documents.",
      targetRef: loanDetailsSectionRef,
    },
  ];

  const validationSchema = Yup.object({
    companyName: Yup.string()
      .min(2, "Company name must be at least 2 characters")
      .required("Company name is required"),
    street: Yup.string().required("Street address is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    zipCode: Yup.string()
      .matches(
        /^\d{5}(-\d{4})?$/,
        "ZIP code must be valid (12345 or 12345-6789)",
      )
      .required("ZIP code is required"),
    registrationType: Yup.string()
      .oneOf(
        ["Registered", "LLC or Corporation to be formed"],
        "Please select registration type",
      )
      .required("Registration type is required"),
    businessPurpose: Yup.string()
      .oneOf(
        ["Purchase", "Refinance", "Startup"],
        "Please select business purpose",
      )
      .required("Business purpose is required"),
  });

  const formik = useFormik({
    initialValues: {
      id: "",
      companyName: "",
      street: "",
      city: "",
      state: "",
      zipCode: "",
      ownershipDetails: "",
      taxIdEin: "",
      registrationType: "Registered",
      businessPurpose: "Purchase",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const submitData = { ...values };
        if (editingBusiness?.id) {
          // Update: keep id in path, send full values
          await updateCustomerBusiness(editingBusiness.id, submitData);
        } else {
          // Create: omit id field entirely from payload
          const { id, ...createData } = submitData;
          const response = await createCustomerBusiness(createData);
          const newId = response?.id || response?.data?.id;
          if (newId) {
            setNewBusinessId(newId);
          }
        }
        await fetchBusinesses();
        setOpenForm(false);
        setEditingBusiness(null);
        formik.resetForm();
      } catch (error:any) {
        setSnackbar({
          open: true,
          message:
            error?.errors.message ||
            error?.message ||
            error?.response?.data?.message ||
            commonMessage.unknownError,
          severity: "error",
        });
      }
    },
  });

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    fetchBusinesses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCustomerId = async () => {
      try {
        const userData = await getLoginUserData();
        if (userData.role === ROLES.CUSTOMER) {
          setCustomerId(userData.id);
        }
      } catch (error) {
        console.error("Error fetching customer ID:", error);
      }
    };
    if (!customerId) {
      fetchCustomerId();
    }
  }, [customerId]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isInitialFetch.current) {
      if (searchTerm) {
        isInitialFetch.current = false;
        timeoutRef.current = setTimeout(() => {
          fetchBusinesses(paginationData.page, searchTerm);
        }, 500);
      }
    } else {
      timeoutRef.current = setTimeout(() => {
        fetchBusinesses(paginationData.page, searchTerm);
      }, 500);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchBusinesses = async (
    page: number = paginationData.page,
    search?: string,
    limit: number = paginationData.limit,
  ) => {
    try {
      setLoading(true);
      const response = await getCustomerBusinesses(search, page, limit);
      if (isInitialFetch.current) {
        isInitialFetch.current = false;
      }
      setBusinesses(response.data || []);
      setPaginationData((prev) => ({
        ...prev,
        page,
        limit,
        total: response.total || 0,
      }));
    } catch (error) {
      console.error("Error fetching businesses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (e: React.ChangeEvent<unknown>, page: number) => {
    fetchBusinesses(page, searchTerm);
  };

  const totalPages = Math.ceil(paginationData.total / paginationData.limit);

  const handleEdit = (business: Business) => {
    formik.setValues(business);
    setEditingBusiness(business);
    setOpenForm(true);
  };

  const handleAddNew = () => {
    setEditingBusiness(null);
    formik.resetForm();
    setOpenForm(true);
  };

  const showTutorial = () => {
    setTutorialStep(0);
    setTutorialOpen(true);
  };

  const closeTutorial = () => {
    setTutorialOpen(false);
  };

  const handleNextTutorialStep = () => {
    if (tutorialStep === tutorialSteps.length - 1) {
      closeTutorial();
      return;
    }
    setTutorialStep((step) => step + 1);
  };

  const handleBackTutorialStep = () => {
    setTutorialStep((step) => Math.max(step - 1, 0));
  };

  if (loading) {
    return (
      <div className="p-12 flex justify-center items-center min-h-[60vh]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="p-0 sm:p-3">
      <Fade in timeout={800}>
        <Grow in timeout={1000}>
          <Box sx={{ mb: 3 }}>
            <div className="bg-white rounded-none sm:rounded-lg shadow-sm border-y sm:border p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search by company name, EIN, or address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-6 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-200"
                    disabled={loading}
                  />
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                  {loading && <CircularProgress size={24} />}

                  <Tooltip title="Add a new business profile" arrow>
                    <Button
                      ref={addBusinessButtonRef}
                      variant="contained"
                      startIcon={
                        !openForm ? (
                          <AddIcon />
                        ) : (
                          <CircularProgress size={20} sx={{ color: "white" }} />
                        )
                      }
                      onClick={handleAddNew}
                      disabled={openForm || loading}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        px: { xs: 2, sm: 4 },
                        py: 1.5,
                        minWidth: { xs: 0, sm: 180 },
                        width: { xs: "100%", sm: "auto" },
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0,0, 0.1), 0 2px 4px -1px rgba(0, 0,0, 0.06)",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow:
                            "0 10px 15px -3px rgba(0, 0,0, 0.1), 0 4px 6px -2px rgba(0, 0,0, 0.05)",
                        },
                      }}
                    >
                      Add New Business
                    </Button>
                  </Tooltip>
                  <Tooltip title="Show page tutorial" arrow>
                    <Button
                      variant="outlined"
                      startIcon={<HelpOutlineIcon />}
                      onClick={showTutorial}
                      disabled={tutorialOpen || loading}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        px: { xs: 2, sm: 3 },
                        py: 1.5,
                        width: { xs: "100%", sm: "auto" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      Show Tutorial
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </div>
          </Box>
        </Grow>
      </Fade>
      <BusinessTable
        businesses={displayedBusinesses}
        onEdit={handleEdit}
        paginationData={paginationData}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        customerId={customerId || undefined}
        newBusinessId={newBusinessId}
        onOpenNewLoan={() => setNewBusinessId("")}
        isTutorialDemo={isTutorialDemo}
        tutorialActiveStep={tutorialStep}
        tutorialRefs={{
          businessCardRef,
          newLoanButtonRef,
          uploadSectionRef,
          loanDetailsSectionRef,
        }}
      />

      <AddBusinessDialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        editingBusiness={editingBusiness}
        formik={formik}
        loading={loading}
      />

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <GuidedTutorialOverlay
        open={tutorialOpen}
        steps={tutorialSteps}
        activeStep={tutorialStep}
        onBack={handleBackTutorialStep}
        onNext={handleNextTutorialStep}
        onClose={closeTutorial}
      />
    </div>
  );
};

export default MyBusinesses;
